import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useDevices } from '../contexts/DeviceContext';
import './DeviceStatusCard.css';

// Format duration in a readable way (e.g., "2min 29s", "1h 5min")
const formatDuration = (seconds: number): string => {
  if (seconds < 0) return '0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}min ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}min ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

interface DeviceStatusCardProps {
  deviceId?: string;
  onClose?: () => void;
  compact?: boolean;
}

const DeviceStatusCard: FC<DeviceStatusCardProps> = ({ deviceId, onClose, compact = false }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Use shared device context for real-time updates
  const { 
    devices, 
    selectedDevice, 
    setSelectedDevice, 
    loading, 
    error, 
    dataSource, 
    refreshDevices,
    lastUpdate
  } = useDevices();

  // Update current time every second for live uptime counter
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-select device based on deviceId prop
  useEffect(() => {
    if (deviceId && devices.length > 0) {
      const device = devices.find(d => d.deviceId === deviceId);
      if (device && (!selectedDevice || selectedDevice.deviceId !== deviceId)) {
        setSelectedDevice(device);
      }
    } else if (!selectedDevice && devices.length > 0) {
      setSelectedDevice(devices[0]);
    }
  }, [deviceId, devices, selectedDevice, setSelectedDevice]);

  // Calculate duration since status changed (online/offline)
  const getTimeSinceStatusChange = (statusChangedAt: string | undefined, lastSeen: string): number => {
    // Use statusChangedAt if available, otherwise fall back to lastSeen
    const timestamp = statusChangedAt || lastSeen;
    const changeDate = new Date(timestamp);
    return (currentTime.getTime() - changeDate.getTime()) / 1000;
  };

  const formatUptime = (uptime: string | null) => {
    if (!uptime) return '-';
    return uptime;
  };

  const getSignalBars = (strength: number | null, bars: number | null, isOnline: boolean) => {
    // No signal if offline
    if (!isOnline) return 0;
    if (bars !== null) return bars;
    if (strength === null) return 0;
    // Convert RSSI to bars (typical range -110 to -50)
    if (strength >= -65) return 5;
    if (strength >= -75) return 4;
    if (strength >= -85) return 3;
    if (strength >= -95) return 2;
    if (strength >= -105) return 1;
    return 0;
  };

  const getProgressColor = (value: number, thresholds = { warning: 70, danger: 90 }) => {
    if (value >= thresholds.danger) return 'var(--color-danger)';
    if (value >= thresholds.warning) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className={`device-status-card ${compact ? 'compact' : ''}`}>
        <div className="device-loading">
          <div className="loading-spinner" />
          <p>Hämtar enhetsstatus...</p>
        </div>
      </div>
    );
  }

  if (error || devices.length === 0) {
    return (
      <div className={`device-status-card ${compact ? 'compact' : ''}`}>
        <div className="device-empty">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8"/>
              <path d="M12 17v4"/>
            </svg>
          </div>
          <h3>Ingen enhet ansluten</h3>
          <p>Väntar på status från RUT956...</p>
          <p className="hint">Konfigurera "Data to Server" på din RUT956</p>
        </div>
      </div>
    );
  }

  const device = selectedDevice || devices[0];

  if (compact) {
    return (
      <div className="device-status-card compact">
        <div className="compact-header">
          <div className="device-online-indicator">
            <span className={`online-dot ${device.isOnline ? 'online' : 'offline'}`} />
            <span className="online-text">{device.isOnline ? 'Online' : 'Offline'}</span>
          </div>
          <span className="device-model">{device.model}</span>
        </div>
        <div className="compact-stats">
          <div className="compact-stat">
            <span className="label">CPU</span>
            <span className="value">
              {device.isOnline && device.cpuUsage != null ? `${device.cpuUsage.toFixed(0)}%` : '-'}
            </span>
          </div>
          <div className="compact-stat">
            <span className="label">Signal</span>
            <span className="value">
              {device.isOnline && device.signalStrength != null ? `${device.signalStrength} dBm` : '-'}
            </span>
          </div>
          <div className="compact-stat">
            <span className="label">Temp</span>
            <span className="value">
              {device.isOnline && device.temperature != null ? `${device.temperature}°C` : '-'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="device-status-card">
      {onClose && (
        <button className="close-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      )}

      {/* Device selector if multiple devices */}
      {devices.length > 1 && (
        <div className="device-selector">
          {devices.map(d => (
            <button
              key={d.deviceId}
              className={`device-tab ${selectedDevice?.deviceId === d.deviceId ? 'active' : ''}`}
              onClick={() => setSelectedDevice(d)}
            >
              <span className={`status-dot ${d.isOnline ? 'online' : 'offline'}`} />
              {d.deviceName}
            </button>
          ))}
        </div>
      )}

      {/* Device Header - Like RMS */}
      <div className="device-header">
        <div className="device-image">
          <div className="device-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M6 21h12"/>
              <path d="M9 17v4"/>
              <path d="M15 17v4"/>
              <circle cx="12" cy="10" r="2"/>
            </svg>
            <span className={`online-badge ${device.isOnline ? 'online' : 'offline'}`}>
              {device.isOnline ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        <div className="device-title">
          <h2>{device.deviceName}</h2>
          <span className="device-model-tag">{device.model}</span>
          <div className={`device-uptime-counter ${device.isOnline ? 'online' : 'offline'}`}>
            <span className="uptime-arrow">{device.isOnline ? '▲' : '▼'}</span>
            <span className="uptime-text">
              {device.isOnline ? 'Online i' : 'Offline i'}{' '}
              <strong>{formatDuration(getTimeSinceStatusChange(device.statusChangedAt, device.lastSeen))}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Main Info Grid - 3 columns like RMS */}
      <div className="device-info-grid">
        {/* Column 1: Device Info */}
        <div className="info-column">
          <div className="info-row">
            <span className="info-label">Serial</span>
            <span className="info-value copyable" onClick={() => copyToClipboard(device.serial || '')}>
              {device.serial || '-'}
              <svg className="copy-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">MAC Address</span>
            <span className="info-value copyable" onClick={() => copyToClipboard(device.macAddress || '')}>
              {device.macAddress || '-'}
              <svg className="copy-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">IMEI</span>
            <span className="info-value copyable" onClick={() => copyToClipboard(device.imei || '')}>
              {device.imei || '-'}
              <svg className="copy-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Firmware</span>
            <span className="info-value">{device.firmware || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Management service</span>
            <span className={`info-value status-badge ${device.isOnline ? 'active' : 'inactive'}`}>
              {device.isOnline ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Column 2: Connection Info */}
        <div className="info-column">
          <div className="info-row">
            <span className="info-label">Mobile status</span>
            <div className="mobile-status-container">
              <div className={`signal-bars ${!device.isOnline ? 'offline' : ''}`}>
                {[1, 2, 3, 4, 5].map(bar => (
                  <div 
                    key={bar} 
                    className={`bar ${getSignalBars(device.signalStrength, device.signalBars, device.isOnline) >= bar ? 'active' : ''}`}
                    style={{ height: `${bar * 4 + 8}px` }}
                  />
                ))}
                <span className="sim-indicator">1</span>
                <span className="sim-indicator dim">2</span>
              </div>
              {device.isOnline && device.signalStrength != null && (
                <span className="signal-dbm">{device.signalStrength} dBm</span>
              )}
            </div>
          </div>
          <div className="info-row">
            <span className="info-label">Ethernet ports</span>
            <div className="ethernet-ports">
              {[1, 2, 3].map(port => (
                <span 
                  key={port} 
                  className={`port ${device.isOnline && device.ethernetPorts?.[port] ? 'active' : ''}`}
                >
                  {port}
                </span>
              ))}
              <span className="port wan">W</span>
            </div>
          </div>
          <div className="info-row">
            <span className="info-label">Connection type</span>
            <span className={`info-value ${device.isOnline ? 'connection-badge' : ''}`}>
              {device.isOnline ? device.connectionType : '-'}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">IP Address</span>
            <span className="info-value">{device.isOnline ? (device.ipAddress || '-') : '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Location</span>
            <span className="info-value">-</span>
          </div>
        </div>

        {/* Column 3: System Stats */}
        <div className="info-column stats-column">
          <div className="stat-row">
            <span className="stat-label">Device uptime</span>
            <span className="stat-value">{device.isOnline ? formatUptime(device.uptime) : '-'}</span>
          </div>
          
          <div className="stat-row">
            <span className="stat-label">CPU usage</span>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ 
                  width: device.isOnline ? `${device.cpuUsage ?? 0}%` : '0%',
                  backgroundColor: getProgressColor(device.cpuUsage ?? 0)
                }}
              />
            </div>
            <span className="stat-value">
              {device.isOnline && device.cpuUsage != null ? `${device.cpuUsage.toFixed(1)} %` : '-'}
            </span>
          </div>
          
          <div className="stat-row">
            <span className="stat-label">RAM usage</span>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ 
                  width: device.isOnline ? `${device.ramUsage ?? 0}%` : '0%',
                  backgroundColor: getProgressColor(device.ramUsage ?? 0)
                }}
              />
            </div>
            <span className="stat-value">
              {device.isOnline && device.ramUsed != null 
                ? `${device.ramUsed.toFixed(2)} / ${device.ramTotal?.toFixed(0) ?? '-'} MB`
                : '-'}
            </span>
          </div>
          
          <div className="stat-row">
            <span className="stat-label">Flash usage</span>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ 
                  width: device.isOnline ? `${device.flashUsage ?? 0}%` : '0%',
                  backgroundColor: getProgressColor(device.flashUsage ?? 0, { warning: 80, danger: 95 })
                }}
              />
            </div>
            <span className="stat-value">
              {device.isOnline && device.flashUsed != null 
                ? `${device.flashUsed.toFixed(2)} / ${device.flashTotal?.toFixed(0) ?? '-'} MB`
                : '-'}
            </span>
          </div>
          
          <div className="stat-row">
            <span className="stat-label">Modem temperature</span>
            <div className="progress-container temp">
              <div 
                className="progress-bar" 
                style={{ 
                  width: device.isOnline && device.temperature != null 
                    ? `${Math.min(device.temperature / 80 * 100, 100)}%` 
                    : '0%',
                  backgroundColor: getProgressColor((device.temperature ?? 0) / 80 * 100, { warning: 50, danger: 75 })
                }}
              />
            </div>
            <span className="stat-value">
              {device.isOnline && device.temperature != null ? `${device.temperature} °C` : '-'}
            </span>
          </div>
          
          <div className="stat-row">
            <span className="stat-label">Modem</span>
            <span className="stat-value modem-name">Quectel EC25-EUX</span>
          </div>
        </div>
      </div>

      {/* Last seen timestamp */}
      <div className="device-footer">
        <div className="footer-left">
          <span className="live-status">
            <span className="live-dot-small" />
            <span className="live-text">Live</span>
            {lastUpdate && (
              <span className="update-time">
                • {new Date(lastUpdate).toLocaleTimeString('sv-SE')}
              </span>
            )}
          </span>
          <span className="last-seen">
            Enhet sedd: {new Date(device.lastSeen).toLocaleString('sv-SE')}
          </span>
          <span className={`data-source ${dataSource}`}>
            {dataSource === 'rms' ? '● RMS Live' : '○ Lokal cache'}
          </span>
        </div>
        <button className="refresh-btn" onClick={refreshDevices}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="m3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          Uppdatera
        </button>
      </div>
    </div>
  );
};

export default DeviceStatusCard;
